/**
 * @fileoverview Edit one linked soft-deadline draft inside the calendar form. The parent owns the collection and update/removal callbacks; this card owns focused field interaction. Timing, category, notes, and assignment remain draft intent until the enclosing event workflow persists them.
 */

import { useSelector } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import Button from "../shared/AppPrimeButton";
import GlassSwitch from './GlassSwitch';
import DateTimeRangePicker from '@wojtekmaj/react-datetimerange-picker';
import DatePicker from 'react-date-picker';
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';



const SoftDeadlineCard = ({ deadline, index, onUpdate, onRemove, categories }) => {
  const availableAttorneys = useSelector((state) => state.calendarInput.availableAttorneys);
  const { tempId, title, date, daterange, allday, selectedCategory, notes, assignedAttorneys } = deadline;

  const categoryOptionTemplate =

  (option) =>
  <div className="flex align-items-center">
            <span className={`inline-flex justify-content-center align-items-center mr-2 w-1rem h-1rem border-circle bg-${option.color}-400`} />
            <span>{option.name}</span>
        </div>;


  const handleDateRangeChange =

  (newRange) => {
    if (newRange && newRange[0] && newRange[1]) {
      onUpdate(tempId, { daterange: [newRange[0].toISOString(), newRange[1].toISOString()] });
    }
  };

  const handleDateChange =

  (newDate) => {
    if (newDate) {
      onUpdate(tempId, { date: newDate.toISOString() });
    }
  };

  const handleAlldayToggle =

  (checked) => {
    const updates = { allday: checked };
    // Sync date fields when toggling
    if (checked && daterange && daterange[0]) {
      updates.date = new Date(daterange[0]).toISOString();
    } else if (!checked && date) {
      const start = new Date(date);
      const end = new Date(start);
      end.setHours(start.getHours() + 1);
      updates.daterange = [start.toISOString(), end.toISOString()];
    }
    onUpdate(tempId, updates);
  };

  return (
    <div className="soft-deadline-card surface-ground border-round p-3 mb-2">
            <div className="flex align-items-center justify-content-between mb-2">
                <span className="text-sm font-semibold text-500">Deadline #{index + 1}</span>
                <Button
          icon="pi pi-trash"
          className="p-button-text p-button-danger p-button-sm"
          onClick={() => onRemove(tempId)}
          tooltip="Remove this deadline"
          tooltipOptions={{ position: 'left' }} />

            </div>

            <div className="grid">
                {/* Title */}
                <div className="field col-12 md:col-6 mb-2">
                    <label className="text-sm font-medium text-900 mb-1 block">Title</label>
                    <InputText
            value={title}
            onChange={(e) => onUpdate(tempId, { title: e.target.value })}
            placeholder="Deadline title"
            className="w-full" />

                </div>

                {/* Category */}
                <div className="field col-12 md:col-6 mb-2">
                    <label className="text-sm font-medium text-900 mb-1 block">Category</label>
                    <Dropdown
            value={selectedCategory}
            onChange={(e) => onUpdate(tempId, { selectedCategory: e.value })}
            options={categories}
            optionLabel="name"
            placeholder="Category"
            itemTemplate={categoryOptionTemplate}
            className="w-full" />

                </div>

                {/* Date with All Day toggle */}
                <div className="field col-12 mb-2">
                    <div className="flex align-items-center gap-3 flex-wrap">
                        <div className="flex align-items-center gap-2">
                            <label className="text-sm font-medium text-900 mb-0">All Day</label>
                            <GlassSwitch
                checked={allday}
                onChange={(e) => handleAlldayToggle(e.value)} />

                        </div>
                        <div className="flex align-items-center gap-2" style={{ position: 'relative', zIndex: 10000 - index }}>
                            <label className="text-sm font-medium text-900 mb-0">
                                {!allday ? 'When:' : 'Date:'}
                            </label>
                            {!allday ?
              <DateTimeRangePicker
                onChange={handleDateRangeChange}
                value={daterange ? [new Date(daterange[0]), new Date(daterange[1])] : null}
                calendarClassName="date-picker-calendar-elevated" /> :


              <DatePicker
                onChange={handleDateChange}
                value={date ? new Date(date) : null}
                calendarClassName="date-picker-calendar-elevated" />}


                        </div>
                    </div>
                </div>

                {/* Assignees */}
                <div className="field col-12 mb-2">
                    <label className="text-sm font-medium text-900 mb-1 block">Assignees</label>
                    <MultiSelect
            value={assignedAttorneys || []}
            onChange={(e) => onUpdate(tempId, { assignedAttorneys: e.value })}
            options={availableAttorneys}
            optionLabel="name"
            placeholder="Select assignees..."
            className="w-full"
            filter
            showClear />

                </div>

                {/* Notes */}
                <div className="field col-12 mb-0">
                    <label className="text-sm font-medium text-900 mb-1 block">Notes</label>
                    <InputTextarea
            value={notes || ''}
            onChange={(e) => onUpdate(tempId, { notes: e.target.value })}
            placeholder="Notes (optional)"
            className="w-full text-sm"
            rows={2}
            autoResize />

                </div>
            </div>
        </div>);

};

export default SoftDeadlineCard;
